import { Connect } from '@genesislcap/foundation-comms';
import { Modal } from '@genesislcap/foundation-ui';
import { customElement, GenesisElement, observable, Tabs } from '@genesislcap/web-core';
import { dealDetailTemplate as template } from './deal-detail.template';
import { dealDetailStyles as styles } from './deal-detail.styles';
import { BondSeries, MunisDeal } from './deal-detail.types';
import { DealSeries } from '../deal-series/deal-series';
import { SplitOrderModal } from '../deal-series/split-order-modal/split-order-modal';
import { MaturityOrder } from '../deal-series/deal-series.types';
import {
  isOrderEligibleForReconfirm,
  mapMaturityOrderForReconfirmation,
} from './deal-reconfirm.utils';
import {
  isOrderPeriodDatesApplicable,
  isOrderPeriodOutsideWindow,
  parseOrderPeriodDateMs,
  isRepriceWindow,
} from '../deal-series/deal-status';
import { computeDealActionEligibility } from './deal-action-eligibility';
import { isOrderPassesSubmissionAmountRules } from '../deal-series/deal-series.util';
import {
  fetchDealSubmitOrders,
  groupSubmitPreviewOrdersBySeriesId,
  areSubmitOrderMapsEqual,
} from '../deal-series/submit-preview-order.util';
import { isOrderValidForCancellation } from './deal-submit-preview/deal-submit-preview-grid.utils';
import { DealSubmitPreview } from './deal-submit-preview/deal-submit-preview';
import { notifyApiError } from '../../../utils/utils';
import { PurposeMapping } from './deal-detail.util';

@customElement({
  name: 'deal-detail',
  template,
  styles,
})
export class DealDetail extends GenesisElement {
  @observable submitActive: boolean;

  @observable cancelActive: boolean;

  @observable submitEnabled: boolean;

  @observable cancelEnabled: boolean;

  @observable reconfirmEnabled: boolean;

  @observable reconfirmActive: boolean;

  /** True when submit/reconfirm must be blocked (PP/ROP outside order window). Does not gate draft entry in the grid. */
  @observable orderPeriodExpired: boolean = false;

  private orderPeriodBeginTimer: ReturnType<typeof setTimeout> | null = null;
  private orderPeriodEndTimer: ReturnType<typeof setTimeout> | null = null;

  previewOrdersModal: Modal;

  submitPreview: DealSubmitPreview;

  cancelSubmitPreview: DealSubmitPreview;

  reviewCancelOrdersModal: Modal;

  reconfirmOrdersModal: Modal;

  @observable informationPanelActive: boolean;

  @observable munisDeal: MunisDeal;

  @observable bondSeriesList: BondSeries[];

  @observable ordersBySeriesId: Map<string, MaturityOrder[]> = new Map();

  @observable submitOrdersBySeriesId: Map<string, MaturityOrder[]> = new Map();

  @observable submitOrdersReady = false;

  @observable submitOrdersVersion = 0;

  private submitOrdersPollInterval: ReturnType<typeof setInterval> | null = null;

  private submitOrdersRefreshInFlight = false;

  @observable deal: any;

  @observable totalDealQuantity: number = 0;

  @observable purposeMappings: PurposeMapping[] = [];

  dealChanged(oldValue: any, newValue: MunisDeal) {
    const dealId = newValue.DEAL_ID;
    if (this.munisDeal && dealId === this.munisDeal.DEAL_ID) {
      // Same deal: tab entry updated from dataserver (listing). Merge all listing fields into munisDeal.
      this.munisDeal = { ...this.munisDeal, ...newValue };
      this.scheduleOrderPeriodExpiry();
      this.setSubmissionCancelStates();
    } else {
      this.initialiseDealDetail(dealId);
    }
    this.informationPanelActive = true;
  }

  @observable loading: boolean = false;

  @observable error: string | null = null;

  @observable activeTabId: string;

  @Connect connect: Connect;

  toggleInformationPanel(): void {
    this.informationPanelActive = !this.informationPanelActive;
  }

  private async fetchDealDetails(dealId: string): Promise<MunisDeal> {
    const deals = await this.fetchData(dealId, 'REQ_MUNIS_DEAL');
    return deals[0] as MunisDeal;
  }

  public async fetchSeries(dealId: string): Promise<BondSeries[]> {
    const series = await this.fetchData(dealId, 'REQ_BOND_SERIES');
    return series as BondSeries[];
  }

  async handleSubmitSuccess(dealId: string): Promise<void> {
    this.shadowRoot
      ?.querySelectorAll('deal-series')
      .forEach((element) => (element as DealSeries).refreshSplitParentRollups());

    await Promise.all([
      this.fetchSeries(dealId).then((series) => {
        this.bondSeriesList = series;
      }),
      this.refreshSubmitOrders(dealId),
    ]);
    this.shadowRoot
      ?.querySelectorAll('split-order-modal')
      .forEach((element) => void (element as SplitOrderModal).kickInTransitStatusSync());
  }

  private async fetchPurposeMappings(): Promise<PurposeMapping[]> {
    const response = await this.connect.request('REQ_MUNI_PURPOSE_SECTOR_MAPPINGS', {});
    if (response?.REPLY?.includes?.('MSG_NACK')) {
      return [];
    }
    return (response.REPLY || []) as PurposeMapping[];
  }

  private async fetchData(dealId: string, resourceName: string) {
    const response = await this.connect.request(resourceName, {
      REQUEST: {
        DEAL_ID: dealId,
      },
    });

    if (response.MESSAGE_TYPE === 'ERROR') {
      const err = `Failed to fetch ${resourceName}`;
      this.error = err;
      console.error(err);
    }

    if (!response.REPLY?.length) {
      const err = 'Empty response';
      this.error = err;
      console.error(err);
    }

    return response.REPLY;
  }

  private async initialiseDealDetail(dealId: string) {
    this.loading = true;
    this.error = null;

    const [deal, series, purposeMappings] = await Promise.all([
      this.fetchDealDetails(dealId),
      this.fetchSeries(dealId),
      this.fetchPurposeMappings(),
    ]);
    this.munisDeal = deal;
    this.bondSeriesList = series;
    this.purposeMappings = purposeMappings;

    // — Init orders map: one entry per series
    this.ordersBySeriesId.clear();
    this.bondSeriesList.forEach(series => {
      this.ordersBySeriesId.set(series.SERIES_ID, []);
    });

    this.scheduleOrderPeriodExpiry();
    this.subscribeSubmitOrders(dealId);
    this.setSubmissionCancelStates();
    if (this.bondSeriesList.length > 0) {
      this.activeTabId = this.bondSeriesList[0].SERIES_ID;
    }
    this.loading = false;
  }

  previewSubmitOrders(): void {
    this.submitPreview?.resetModalState();
    this.previewOrdersModal.show();
    this.submitActive = true;
    this.startSubmitOrdersPolling();
    requestAnimationFrame(() => this.submitPreview?.prepareForOpen());
  }

  closeSubmitOrders(): void {
    this.stopSubmitOrdersPolling();
    this.submitPreview?.resetModalState();
    this.previewOrdersModal.close();
    this.submitActive = false;
    this.refreshButtonStatesAfterAction();
  }

  reviewCancelOrders(): void {
    this.cancelSubmitPreview?.resetModalState();
    this.reviewCancelOrdersModal.show();
    this.cancelActive = true;
    this.startSubmitOrdersPolling();
    requestAnimationFrame(() => this.cancelSubmitPreview?.prepareForOpen());
  }

  closeReviewCancelOrdersModal(): void {
    this.stopSubmitOrdersPolling();
    this.cancelSubmitPreview?.resetModalState();
    this.reviewCancelOrdersModal.close();
    this.cancelActive = false;
    this.refreshButtonStatesAfterAction();
  }

  /** Recalculate button states after modal close: once immediately with current data, and again after a delay to catch late-arriving dataserver updates. */
  private refreshButtonStatesAfterAction(): void {
    this.setSubmissionCancelStates();
    setTimeout(() => this.setSubmissionCancelStates(), 1500);
  }

  showReconfirmModal(): void {
    this.reconfirmOrdersModal.show();
    this.reconfirmActive = true;
  }

  closeReconfirmModal(): void {
    this.reconfirmOrdersModal.close();
    this.reconfirmActive = false;
  }

  async reconfirmAllOrders(): Promise<void> {
    const ordersToReconfirm = this.getOrdersEligibleForReconfirm();
    if (ordersToReconfirm.length === 0 || !this.munisDeal) return;

    // — Only send orders that pass base submission amount rules (base conditions block submit)
    const ordersValidForSubmit = ordersToReconfirm.filter(isOrderPassesSubmissionAmountRules);
    if (ordersValidForSubmit.length === 0) return;

    const MUNI_ORDER_LIST = ordersValidForSubmit.map(mapMaturityOrderForReconfirmation);
    try {
      const response = await this.connect.commitEvent('EVENT_SUBMIT_MUNI_ORDERS', {
        DETAILS: { MUNI_ORDER_LIST },
      });
      if (response.ERROR?.length) {
        notifyApiError(response.ERROR[0]);
        console.error('Error reconfirming orders', response.ERROR);
      } else {
        this.reconfirmEnabled = false;
        this.submitEnabled = false;
        this.cancelEnabled = false;
        this.closeReconfirmModal();
        await this.fetchSeries(this.munisDeal.DEAL_ID);
        this.setSubmissionCancelStates();
      }
    } catch (e) {
      console.error('Error reconfirming orders', e);
    }
  }

  handleTabChanged(e: CustomEvent): void {
    this.activeTabId = (e.currentTarget as Tabs).activeid;
    window.dispatchEvent(new Event('resize')); // — nudge grid after tab switch
  }

  handleOrdersChanged(e: CustomEvent<{seriesId: string, orders: MaturityOrder[]}>) {
    const { seriesId, orders } = e.detail;
    const newOrders = orders || [];
    const existingCount = this.ordersBySeriesId.get(seriesId)?.length ?? 0;
    // Avoid overwriting with empty when grid re-inits after deal/series refresh (e.g. 30s) and emits empty before data loads; preserve existing orders so Submit/Cancel stay correct.
    if (newOrders.length > 0 || existingCount === 0) {
      this.ordersBySeriesId.set(seriesId, newOrders);
      this.ordersBySeriesId = new Map(this.ordersBySeriesId);
      this.setSubmissionCancelStates();
      this.updateDealTotals();
      if (this.munisDeal?.DEAL_ID != null) {
        void this.refreshSubmitOrders(this.munisDeal.DEAL_ID);
      }
      if (this.submitActive) {
        this.submitPreview?.syncGridsFromSubmitOrders();
      }
      if (this.cancelActive) {
        this.cancelSubmitPreview?.syncGridsFromSubmitOrders();
      }
    }
  }

  private startSubmitOrdersPolling(): void {
    this.stopSubmitOrdersPolling();
    const dealId = this.munisDeal?.DEAL_ID;
    if (dealId == null) return;

    void this.refreshSubmitOrders(dealId);
    this.submitOrdersPollInterval = setInterval(() => {
      void this.refreshSubmitOrders(dealId);
    }, 5_000);
  }

  private stopSubmitOrdersPolling(): void {
    if (this.submitOrdersPollInterval) {
      clearInterval(this.submitOrdersPollInterval);
      this.submitOrdersPollInterval = null;
    }
  }

  private async refreshSubmitOrders(dealId: string | number): Promise<void> {
    if (this.submitOrdersRefreshInFlight) return;
    this.submitOrdersRefreshInFlight = true;
    try {
      const orders = await fetchDealSubmitOrders(this.connect, dealId);
      const nextMap = groupSubmitPreviewOrdersBySeriesId(orders);
      if (areSubmitOrderMapsEqual(this.submitOrdersBySeriesId, nextMap)) {
        return;
      }
      this.submitOrdersBySeriesId = nextMap;
      this.submitOrdersReady = true;
      this.submitOrdersVersion++;
      this.notifySubmitPreviewGrids();
      this.setSubmissionCancelStates();
    } catch (e) {
      console.error('Failed to fetch REQ_DEAL_SUBMIT_ORDERS', e);
    } finally {
      this.submitOrdersRefreshInFlight = false;
    }
  }

  private notifySubmitPreviewGrids(): void {
    if (this.submitActive) {
      this.submitPreview?.syncGridsFromSubmitOrders(true);
    }
    if (this.cancelActive) {
      this.cancelSubmitPreview?.syncGridsFromSubmitOrders(true);
    }
  }

  private subscribeSubmitOrders(dealId: string): void {
    this.submitOrdersReady = false;
    this.submitOrdersBySeriesId = new Map();
    void this.refreshSubmitOrders(dealId);
  }

  private getSubmitOrdersFlat(): MaturityOrder[] {
    return Array.from(this.submitOrdersBySeriesId.values()).flat();
  }

  /** Emit reconfirm eligibility to route so Deal Details tab only blinks when deal has orders eligible for reconfirm. */
  private emitReconfirmEligibility(): void {
    if (!this.munisDeal) return;
    const dealId = this.munisDeal.DEAL_ID;
    const orders = this.submitOrdersReady ? this.getSubmitOrdersFlat() : this.getAllOrders();
    const hasReconfirmEligibleOrders = orders.some((order) => isOrderEligibleForReconfirm(order));
    this.$emit('reconfirm-eligibility-changed', {
      dealId: String(dealId),
      hasReconfirmEligibleOrders,
    });
  }

  private setSubmissionCancelStates(): void {
    const orders = this.submitOrdersReady ? this.getSubmitOrdersFlat() : this.getAllOrders();
    const eligibility = computeDealActionEligibility({
      dealStatus: this.munisDeal?.DEAL_STATUS,
      orderPeriodBeginDate: this.munisDeal?.ORDER_PERIOD_BEGIN_DATE,
      orderPeriodEndDate: this.munisDeal?.ORDER_PERIOD_END_DATE,
      orders,
    });

    this.orderPeriodExpired = eligibility.orderPeriodOutsideWindow;
    this.submitEnabled = eligibility.submitEnabled;
    this.cancelEnabled = eligibility.cancelEnabled;
    this.reconfirmEnabled = eligibility.reconfirmEnabled;
    this.emitReconfirmEligibility();
  }

  isInRepriceWindow(): boolean {
    return isRepriceWindow(this.munisDeal?.DEAL_STATUS);
  }
 
  /** True when at least one order is eligible for re-confirmation. Gates Edit in reprice window; per-row reset and Clear All use row-level checks. */
  get dealHasEligibleOrdersToReset(): boolean {
    return this.getAllOrders().some((order) => isOrderEligibleForReconfirm(order));
  }

  /** — All orders from grid (all series). */
  private getAllOrders(): MaturityOrder[] {
    return Array.from(this.ordersBySeriesId.values()).flat();
  }

  private updateDealTotals(): void {
    this.totalDealQuantity = this.getAllOrders().reduce((acc, o) => acc + (parseFloat(o.QUANTITY) || 0), 0);
  }

  /** — Orders that can be sent with Reconfirm All (price-changed, instructed, not reconfirmed, amount ≤ submitted). */
  private getOrdersEligibleForReconfirm(): MaturityOrder[] {
    return this.getAllOrders().filter((order) => isOrderEligibleForReconfirm(order));
  }

  /** — For Cancel modal: in reconfirm window only orders that can be reconfirmed; otherwise all orders (grid still filters by status). */
  getCancelOrdersBySeriesId(): Map<string, MaturityOrder[]> {
    const all = this.ordersBySeriesId;
    if (!all?.size) return new Map();
    if (!this.isInRepriceWindow()) return all;
    const result = new Map<string, MaturityOrder[]>();
    all.forEach((orders, seriesId) => {
      const filtered = orders.filter(
        (order) => isOrderValidForCancellation(order) && isOrderEligibleForReconfirm(order),
      );
      if (filtered.length) result.set(seriesId, filtered);
    });
    return result;
  }

  get flexMaturityOptions(): MaturityOrder[] {
    return this.getAllOrders();
  }

  /** Schedule callbacks at ORDER_PERIOD_BEGIN_DATE and ORDER_PERIOD_END_DATE. Only for Preliminary Pricing / Retail Order Period; same gate as isOrderPeriodOutsideWindow / backend validateOrderPeriodNotEnded. */
  private scheduleOrderPeriodExpiry(): void {
    if (this.orderPeriodBeginTimer) { clearTimeout(this.orderPeriodBeginTimer); this.orderPeriodBeginTimer = null; }
    if (this.orderPeriodEndTimer) { clearTimeout(this.orderPeriodEndTimer); this.orderPeriodEndTimer = null; }

    if (!isOrderPeriodDatesApplicable(this.munisDeal?.DEAL_STATUS)) {
      this.orderPeriodExpired = false;
      return;
    }

    const beginDate = this.munisDeal?.ORDER_PERIOD_BEGIN_DATE;
    const endDate = this.munisDeal?.ORDER_PERIOD_END_DATE;

    this.orderPeriodExpired = isOrderPeriodOutsideWindow(
      this.munisDeal?.DEAL_STATUS,
      beginDate,
      endDate,
    );

    const beginMs = parseOrderPeriodDateMs(beginDate);
    const endMs = parseOrderPeriodDateMs(endDate);
    if (beginMs == null || endMs == null) return;

    const now = Date.now();

    if (beginMs > now) {
      this.orderPeriodBeginTimer = setTimeout(() => {
        this.scheduleOrderPeriodExpiry();
        this.setSubmissionCancelStates();
      }, beginMs - now);
    }

    if (endMs > now) {
      this.orderPeriodEndTimer = setTimeout(() => {
        this.scheduleOrderPeriodExpiry();
        this.setSubmissionCancelStates();
      }, endMs - now);
    }
  }

  disconnectedCallback(): void {
    if (this.orderPeriodBeginTimer) { clearTimeout(this.orderPeriodBeginTimer); this.orderPeriodBeginTimer = null; }
    if (this.orderPeriodEndTimer) { clearTimeout(this.orderPeriodEndTimer); this.orderPeriodEndTimer = null; }
    this.stopSubmitOrdersPolling();
    super.disconnectedCallback();
  }
}
